// @angular
import {
  HostListener,
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { NgClass } from "@angular/common";
import { Router, RouterOutlet } from "@angular/router";
// Translate
import { TranslateModule } from "@ngx-translate/core";
// Spinner
import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";
// Own services
import { BrowserStorageLocalService } from "../../../services/shared/BrowserStorageServices/BrowserStorageLocalService.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { ReloadComponentService } from "../../../services/shared/ReloadComponentService.service";
import { TemplateService } from "../../../services/shared/TemplateService.service";
import { ManufacturerService } from "../../../services/shared/ManufacturerService.service";
import { RouteCheckService } from "../../../services/shared/RouteCheckService.service";
// Interfaces
import { SessionData } from "../../../interfaces/SessionGlobalInterface.type";
import { LANGUAGE } from "../../../services/language/LanguageController.service";
import { PROFILES } from "../../../../assets/profiles/profiles";
// Components
import { FooterbarComponent } from "../../nav-module/footerbar/footerbar.component";
import { RequestProgressPanelComponent } from "../../task-module/request-progress-panel/request-progress-panel.component";
import { SubscriberNavComponent } from "../../subscriber-module/subscriber-nav/subscriber-nav.component";
import { NavbarSideComponent } from "../../nav-module/navbar-side/navbar-side.component";
import { NavbarTopComponent } from "../../nav-module/navbar-top/navbar-top.component";
import { SnakeComponent } from "../../games-module/snake/snake.component";
import { ChatbotComponent } from "../../../global/chatbot/chatbot.component";

/**
 * Authenticated app shell: top and side navbars (subscriber nav for subscribers), router outlet, footer,
 * request progress panel, chatbot and loading spinner. F4 reloads the current component.
 * Session data comes in as inputs from the root component; logoutFlag is emitted upward.
 */
@Component({
  selector: "app-main-panel",
  templateUrl: "./main-panel.component.html",
  styleUrls: ["./main-panel.component.scss"],
  providers: [RouteCheckService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgxSpinnerModule,
    NgClass,
    SnakeComponent,
    NavbarTopComponent,
    NavbarSideComponent,
    SubscriberNavComponent,
    RequestProgressPanelComponent,
    RouterOutlet,
    FooterbarComponent,
    TranslateModule,
    ChatbotComponent
  ]
})
export class MainPanelComponent implements OnInit, OnDestroy, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Session data stream (handled with effect)
  manufacturerDataReady: boolean = false;
  componentInititated: boolean = false;
  logoImgUrl: string;

  @Input() startUrl: string;
  @Input() profileStartUrl: string;
  @Input() sessionUser: string;
  @Input() sessionProfile: string;
  private _lastSeenProfile: string | null = null;
  @Input() sessionLanguage: string;
  @Input() loadSession: boolean;
  @Input() appVersion: { module: string; version: string }[];
  @Output() logoutFlag = new EventEmitter<any>();
  sessionData: SessionData;
  readonly PROFILES = PROFILES;

  // Loading spinner
  disableSpinner: boolean;
  spinnerText: string;
  spinnerHasError: boolean = false;
  showGame: boolean = false;
  @ViewChild("elseBlock") elseBlock: TemplateRef<any>;

  // Side bar
  sidebarLocked: boolean;

  // Responsive control
  hideDesktopMenus: boolean = false;
  showResponsiveMenuFlag: boolean = false;
  @HostListener("window:resize")
  onResize() {
    this.hideDesktopMenus = window.innerWidth < 980;
  }

  // Key combination listener to reload the component
  @HostListener("window:keydown.F4")
  onKeyDown() {
    this.ReloadComponentService.reload();
  }

  // Allow drop on window
  enableDrop: boolean;

  // Game
  gameActive: boolean;

  // Chatbot
  chatbotEnabled: boolean = false;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/
  constructor(
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly ManufacturerService: ManufacturerService,
    private readonly ReloadComponentService: ReloadComponentService,
    private readonly RouteCheckService: RouteCheckService,
    private readonly router: Router,
    private readonly spinner: NgxSpinnerService,
    public SessionDataService: SessionDataService,
    private readonly TemplateService: TemplateService
  ) {
    this._lastSeenProfile = untracked(() => this.SessionDataService.profile());
    // Manufacturers ready
    effect(() => {
      const manufacturers = this.ManufacturerService.manufacturerList();
      if (manufacturers !== null && !this.componentInititated) {
        untracked(() => this.loadComponent());
        this.manufacturerDataReady = true;
      }
    });
    // Language
    effect(() => {
      const language = this.SessionDataService.language();
      if (language) {
        this.sessionLanguage = language;
      }
    });
    // Profile change (force reload, skips first load)
    effect(() => {
      const profile = this.SessionDataService.profile();
      if (profile && profile !== this._lastSeenProfile) {
        this.ReloadComponentService.reload();
      }
      this._lastSeenProfile = profile;
    });
    // Spinner
    effect(() => {
      this.disableSpinner = this.SessionDataService.disableSpinner();
    });
    effect(() => {
      this.spinnerText = this.SessionDataService.spinnerText();
    });
    effect(() => {
      this.spinnerHasError = this.SessionDataService.spinnerHasError();
    });
    // Reload panel
    effect(() => {
      if (this.SessionDataService.reloadPanelTick() > 0) {
        this.reloadPanel();
      }
    });
    // Drag and drop
    effect(() => {
      const data = this.SessionDataService.componentData();
      if (data?.enableDrop != null) {
        this.enableDrop = data.enableDrop;
      }
    });
    // Active game
    effect(() => {
      this.gameActive = this.SessionDataService.gameActive();
    });
    // Chatbot
    effect(() => {
      this.chatbotEnabled = this.SessionDataService.chatbotEnabled();
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    // Load initial values
    this.sessionProfile = untracked(() => this.SessionDataService.profile());

    // Load manufacturers, communications and metrology
    this.ManufacturerService.getServerData();

    // Screen width control
    this.hideDesktopMenus = window.innerWidth < 980;
  }
  /***************************************************************************/
  // ANCHOR After-render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    this.TemplateService.add("elseBlock", this.elseBlock);
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  loadComponent(): void {
    // Check initial url
    if (this.loadSession) {
      this.sessionData = this.BrowserStorageLocalService.getJsonValue("session");
      if (this.sessionData.userId) {
        // Last login
        this.SessionDataService.sendLastLoginDate(this.sessionData.lastLogin);
        // Number format
        this.SessionDataService.sendNumberFormat(
          this.sessionData.numberFormat ??
            (this.sessionData.language === LANGUAGE.INGLES ? LANGUAGE.INGLES : LANGUAGE.ESPANOL)
        );
        // Date format
        this.SessionDataService.sendDateFormat(this.sessionData.dateFormat ?? "DD/MM/YYYY");
        // Redirect
        this.SessionDataService.sendUrlReloadFlag(true);
        const targetUrl =
          this.startUrl && this.startUrl !== "/" && this.startUrl !== ""
            ? this.startUrl
            : this.sessionData.url;
        const state = {
          state: { data: this.sessionData.urlData }
        };
        if (this.sessionProfile != PROFILES.ABONADO)
          this.RouteCheckService.subscribeToNavigation(targetUrl, state);
      } else {
        this.logoutFlag.emit();
      }
    } else if (this.sessionProfile != PROFILES.ABONADO) {
      this.RouteCheckService.subscribeToNavigation(this.startUrl);
    } else if (this.sessionProfile === PROFILES.ABONADO) {
      this.router.navigate([this.startUrl]);
    }
    this.componentInititated = true;
  }

  logoImgUrlUpdate(url: any): void {
    this.logoImgUrl = url;
  }

  logout(): void {
    this.logoutFlag.emit();
  }

  // Dismiss spinner error — hides spinner and clears error state
  dismissSpinnerError(): void {
    this.SessionDataService.clearSpinnerError();
    this.spinner.hide("spinner-hard");
    this.spinner.hide("spinner-soft");
  }

  // Reload the page after a hard HTTP error
  reloadPage(): void {
    globalThis.location.reload();
  }

  // Reload panel
  reloadPanel(): void {
    this.ReloadComponentService.reload();
  }
}
